import { useContext, useEffect, useMemo, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "iuvity-ui-components/dist/column";
import { FormSkeleton } from "..";
import { TableHeader } from "../datatable/TableHeader";
import { Actions } from "../datatable/Actions";
import { DialogProps } from "../dialog/CustomDialog";
import { Property } from "../../interfaces";
import { AppContext } from "../../context/index";
import { useLocation, useNavigate } from "react-router-dom";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

interface PropertiesInfoProps {
  title: string;
  type: "product" | "transaction";
  data: null | any[];
  options: any;
  actionFn: Function;
  reloadFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

export const PropertiesInfo: React.FC<PropertiesInfoProps> = ({
  title,
  data,
  options,
  actionFn,
  reloadFn,
  type,
  auxModalController,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const typeItem = type === "transaction" ? "Transactions" : "Products";

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "key",
          header: t(`${typeItem}.keyLabel`),
        },
        {
          field: "labelkey",
          header: t(`${typeItem}.labelkeyLabel`),
        },
        {
          field: "description",
          header: t(`${typeItem}.descriptionLabel`),
        },
        {
          field: "defaultvalue",
          header: t(`${typeItem}.defaultvalueLabel`),
        },
        {
          field: "isdefaultforall",
          header: t(`${typeItem}.isdefaultforallLabel`),
        },
        {
          field: "type",
          header: t(`${typeItem}.proptypeLabel`),
        },
        {
          field: "usagelist",
          header: t(`${typeItem}.usagelistLabel`),
        },
        {
          field: "releaseversion",
          header: t(`${typeItem}.releaseLabel`),
        },
        {
          field: "actions",
          header: t(`${typeItem}.actionsLabel`),
        },
      ];
    } else {
      return [];
    }
  }, [t, language, typeItem]);

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onDeleteAction = async (prop: Property) => {
    await actionFn("DELETE", prop.id);
    reloadFn();
  };

  const handleAuxModal = async (prop: Property) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t(`${typeItem}.propDeleteModalTitle`),
      content: `${t(`${typeItem}.propDeleteModalContent1`)} "${prop.key}" ${t(
        `${typeItem}.propDeleteModalContent2`
      )}`,
      onConfirm: () => onDeleteAction(prop),
    }));
  };

  const actionEditFn = (prop?: Property) => {
    if (prop) {
      setAppState((prev) => ({
        ...prev,
        selectedProperty: prop,
      }));
      navigate(`${location.pathname}/editproperty`);
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    if (language) {
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(
            `${typeItem}.${col.field === "type" ? "prop" : ""}${col.field}Label`
          ),
        }))
      );
    }
  }, [t, language, setSelectedColumns, typeItem]);

  return (
    <>
      <SubheaderActions
        className="px-0 pt-4 pb-3"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Transactions.newTransactionPropertyTitle"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate(`${location.pathname}/newproperty`)
          },
        ])}
      />

      {data ? (
        <DataTable
          header={
            <TableHeader
              title={title}
              columns={columns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={filters}
              setFilters={setFilters}
              reload={reloadFn}
            />
          }
          currentPageReportTemplate={t(
            "ConsoleTranslations.rowsPaginatorTemplateLabel"
          )}
          paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
          emptyMessage={t("TableActions.emptyMessage")}
          rows={10}
          size="normal"
          filters={filters}
          sortField="id"
          dataKey="id"
          sortOrder={1}
          value={data.map((prop: Property) => {
            return {
              ...prop,
              isdefaultforall: t(
                `${typeItem}.prop${prop.isdefaultforall}Label`
              ),
              type: t(
                `GlobalConfigurations.${
                  prop?.type === "number" ? "numeric" : prop?.type
                }OptionLabel`
              ),
              usagelist: !prop.usagelist
                ? t(`${typeItem}.propallLabel`)
                : prop.usagelist.join(", "),
              actions: (
                <Actions
                  enableDelete={!!prop.createdby}
                  onEdit={() => actionEditFn(prop)}
                  onDelete={() => handleAuxModal(prop)}
                  visibleObject={{...permissiondInfoByRole}}
                />
              ),
            };
          })}
          scrollable
          resizableColumns
        >
          {selectedColumns.map((col) => {
            return (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={col.field !== "actions"}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? {
                      justifyContent: "center",
                      display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                    }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field !== "actions") ? "10rem" : "",
                  display: (col.field === "actions")
                    ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                    : undefined,
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby')}
              />
            );
          })}
        </DataTable>
      ) : (
        <FormSkeleton length={8} />
      )}
    </>
  );
};
